import { useCallback, useSyncExternalStore } from 'react'
import { useCurrentUser } from './useCurrentUser'

export const MAX_COMPARED = 5

const EMPTY: string[] = []
const listeners = new Set<() => void>()
// useSyncExternalStore needs a stable snapshot, so parsed lists are cached per key.
const cache = new Map<string, { raw: string | null; ids: string[] }>()

const storageKey = (userId: string) => `dyeatlas.comparison.${userId}`

function read(key: string): string[] {
  const raw = window.localStorage.getItem(key)
  const cached = cache.get(key)
  if (cached && cached.raw === raw) return cached.ids

  let ids = EMPTY
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : []
    if (Array.isArray(parsed)) {
      ids = parsed.filter((id): id is string => typeof id === 'string').slice(0, MAX_COMPARED)
    }
  } catch {
    ids = EMPTY
  }
  cache.set(key, { raw, ids })
  return ids
}

function write(key: string, ids: string[]) {
  window.localStorage.setItem(key, JSON.stringify(ids.slice(0, MAX_COMPARED)))
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  // Keeps two open tabs on the same selection.
  window.addEventListener('storage', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', listener)
  }
}

/**
 * The records picked for comparison. Kept per user in local storage so the
 * selection survives leaving the database view, and so the colour space can plot it.
 */
export function useComparisonList() {
  const { user } = useCurrentUser()
  const key = user ? storageKey(user.id) : null

  const ids = useSyncExternalStore(subscribe, () => (key ? read(key) : EMPTY))

  const set = useCallback((next: string[]) => key && write(key, next), [key])

  const toggle = useCallback(
    (id: string) => {
      if (!key) return
      const current = read(key)
      if (current.includes(id)) write(key, current.filter((entry) => entry !== id))
      else if (current.length < MAX_COMPARED) write(key, [...current, id])
    },
    [key],
  )

  const clear = useCallback(() => key && write(key, []), [key])

  return { ids, set, toggle, clear }
}
